import HelpIcon from '../help-icon';
import { cn } from '../../../lib/utils';

/** A field label with an optional tooltip for the details. */
export function FieldLabel({
  htmlFor,
  help,
  children,
}: {
  htmlFor?: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <label
        htmlFor={htmlFor}
        className="text-sm font-medium text-prompt-safecomvulcan"
      >
        {children}
      </label>
      {help && <HelpIcon content={help} size={15} />}
    </span>
  );
}

export interface Choice<T extends string> {
  value: T;
  label: string;
}

/** Two or three mutually exclusive options, shown side by side. */
export function ChoiceGroup<T extends string>({
  label,
  choices,
  value,
  onChange,
}: {
  label: string;
  choices: Choice<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex max-w-full self-start overflow-hidden rounded-md border border-[#CED4DA] bg-prompt-safecomwhite"
    >
      {choices.map((choice, index) => {
        const selected = choice.value === value;
        return (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(choice.value)}
            className={cn(
              'px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-prompt-safecomoutrageous-orange',
              index > 0 && 'border-l border-[#CED4DA]',
              selected
                ? 'bg-[#FFEDE8] font-medium text-prompt-safecomoutrageous-orange'
                : 'text-prompt-safecomabbey hover:text-prompt-safecomvulcan',
            )}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}

/** A field's problem under it, or a softer note that doesn't block. */
export function FieldMessage({
  id,
  error,
  warning,
}: {
  id: string;
  error?: string;
  warning?: string;
}) {
  if (error) {
    return (
      <p id={id} className="text-xs text-red-700">
        {error}
      </p>
    );
  }
  if (warning) {
    return (
      <p id={id} className="text-xs text-amber-700">
        {warning}
      </p>
    );
  }
  return null;
}
